import {html, render} from '@isceco/widget-library2/external/lit'
import WebComponent from '../WebComponent.js'
import FileUploadService from '../FileUploadService'
import ConfigUtil from '../services/ConfigUtil'
import {EformFileInputService} from '../services/EformFileInputService'
import FormConfigService from '../services/FormConfigService'
import EformSubmitStatus from './SubmitStatus/EformSubmitStatus'
import '@isceco/widget-library2/basic-elements/Title/Title'
import './Captcha/Turnstile.js'

export default class FormComponent extends WebComponent {

  constructor() {
    super()
    this.preview = false
    this.fileInputService = null
    this.isWhitelisted = null
    this.fileMaxSize = null
    this.requiresCaptcha = false
    this.showCaptchaModal = false
    this.currentCaptchaToken = null
    this.needsHiddenCaptcha = false // only true when we need to refresh
  }

  connectedCallback() {
    super.connectedCallback()

    this.requiresCaptcha = this.checkIfRequiresCaptcha(getNavigationHash())

    // For Playwright: skip CAPTCHA entirely, use dummy token
    if (this.requiresCaptcha && navigator.webdriver) {
      this.currentCaptchaToken = 'XXXX.DUMMY.TOKEN.XXXX'
      this.requiresCaptcha = false // Disable CAPTCHA system entirely for tests
    } else if (this.requiresCaptcha) {
      this.showCaptchaModal = true
    }

    const fileUploadService = new FileUploadService()
    const configUtil = new ConfigUtil()
    const formConfigService = new FormConfigService()

    this.initialized = Promise.all([
      fileUploadService.getFileUploadUrl(),
      configUtil.retrieveConfiguration(),
      formConfigService.read(getNavigationHash(), 'allowed')
    ]).then(([fileUploadUrl, config, isWhitelisted]) => {
      // Refresh the captcha token right before every upload (tokens are single-use with a 5 min TTL)
      const refreshCaptchaFn = () => this.getNewToken()
      this.fileInputService = new EformFileInputService(fileUploadUrl, crypto.randomUUID(), refreshCaptchaFn)
      this.fileMaxSize = config.fileMaxSize
      this.isWhitelisted = isWhitelisted
    }).catch(_ => {
      this.isWhitelisted = false
    })
  }

  // Override WebComponent.render() method
  render() {
    Promise.all([this.initialized, this.translationLoaded]).then(_ => {
      if(!this.isWhitelisted) {
        render(this.renderNotAuthorized(), this.root)
      } else {
        // Check if we need to show initial captcha modal
        if (this.requiresCaptcha && this.showCaptchaModal) {
          render(this.renderCaptchaModal(), this.root)
        } else {
          this.renderTemplate()
        }
      }
    })
  }

  renderNotAuthorized() {
    return html`
      <isceco-title id="page-title"
                    text="${this.i18n.translate('page.title')}"
      ></isceco-title>
      <p id="not-authorized-text">${translateText(this._translations(), 'not_authorized')}</p>
    `
  }

  // Generic abort method
  abort() {
    if (this.preview) {
      this.preview = false
      send(EformSubmitStatus.EVENT.CLOSE, {})
      this.render()
    }
  }

  checkIfRequiresCaptcha(formId) {
    const anonymousForms = window.eformConfig?.anonymousForms
    if (!anonymousForms) {
      return false
    }
    // Handle both comma-separated string and array formats
    const formsList = typeof anonymousForms === 'string'
      ? anonymousForms.split(',').map(f => f.trim())
      : anonymousForms
    return formsList.includes(formId)
  }

  shouldSkipHiddenCaptcha() {
    return !this.requiresCaptcha || this.showCaptchaModal || !this.needsHiddenCaptcha
  }

  renderCaptcha() {
    // Only render hidden captcha when we're actively refreshing (after file upload)
    // This prevents double token generation on initial load
    if (this.shouldSkipHiddenCaptcha()) {
      return html``
    }

    const captchaComponent = html`<eform-turnstile @captcha-token="${this._onCaptchaToken}"></eform-turnstile>`

    // Render hidden for token refresh capability
    return html`
      <div style="display: none;">
        ${captchaComponent}
      </div>
    `
  }

  _getAttributeFromTokenParsed = attr => window?.keycloak?.tokenParsed?.[attr];

  _onCaptchaToken = event => {
    // Captcha token received - store it
    this.currentCaptchaToken = event.detail.token
    // Subsequent tokens must not trigger a full re-render.
    if (this.showCaptchaModal) {
      this.showCaptchaModal = false
      this.needsHiddenCaptcha = true
      this.render()
    }
  }

  renderCaptchaModal() {
    const captchaComponent = html`<eform-turnstile @captcha-token="${this._onCaptchaToken}"></eform-turnstile>`

    return html`
      <style>
        .captcha-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
        }
        .captcha-modal {
          background: white;
          border-radius: 8px;
          padding: 2rem;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        }
        .captcha-modal h2 {
          margin-top: 0;
          margin-bottom: 1.5rem;
          color: #333;
          font-size: 1.5rem;
        }
        .captcha-modal-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
      </style>
      <div class="captcha-modal-overlay">
        <div class="captcha-modal">
          <h2>🛡️ ${translateText(this._translations(), 'captcha_title')}</h2>
          <div class="captcha-modal-content">
            ${captchaComponent}
          </div>
        </div>
      </div>
    `
  }

  isCaptchaReady() {
    // If captcha not required, always ready
    if (!this.requiresCaptcha) {
      return true
    }
    // Otherwise check if token is available
    return this.currentCaptchaToken !== null
  }

  async getNewToken() {
    // The hidden widget is always mounted by the time we get here (the initial
    // captcha modal must be solved before the form can be uploaded to / submitted).
    const captchaComponent = this.querySelector('eform-turnstile')
    if (!captchaComponent) {
      return this.currentCaptchaToken
    }

    try {
      const token = await captchaComponent.executeRefresh()
      this.currentCaptchaToken = token
      return token
    } catch (error) {
      this.logging.log(error)
      this.currentCaptchaToken = null
      return null
    }
  }

  _translations = () => ({
    de: {
      not_authorized: 'Um dieses Formular zu sehen und zu verwenden, müssen Sie angemeldet sein und die erforderlichen Berechtigungen haben.',
      captcha_title: 'Sicherheitsüberprüfung'
    },
    fr: {
      not_authorized: 'Pour consulter et utiliser ce formulaire, vous devez être connecté et disposer des autorisations nécessaires.',
      captcha_title: 'Vérification de sécurité'
    },
    it: {
      not_authorized: 'Per visualizzare e utilizzare questo modulo, è necessario effettuare l\'accesso e disporre delle autorizzazioni necessarie.',
      captcha_title: 'Verifica di sicurezza'
    },
    en: {
      not_authorized: 'To view and use this form, you must be logged in and have the necessary permissions.',
      captcha_title: 'Security Verification'
    }
  })
}
